import { Box, Flex, Text } from '@chakra-ui/react'
import { Link } from '@tanstack/react-router'
import { Tooltip } from '@/shared/ui/tooltip'
import {
  sidebarStyles,
  sidebarTooltipContentProps,
  sidebarTooltipPositioning,
} from './styles'

interface NavItemProps {
  icon?: React.ElementType
  label: string
  path?: string
  search?: Record<string, string>
  open: boolean
  active?: boolean
  /** Nested tab-style entry: no icon, marked by a left bar when active. */
  sub?: boolean
  onClick?: () => void
}

export function NavItemContent({
  open,
  icon: Icon,
  label,
  active,
  sub,
}: Pick<NavItemProps, 'icon' | 'label' | 'active' | 'open' | 'sub'>) {
  if (sub) {
    return (
      <Flex
        direction="row"
        align="center"
        gap="12px"
        py={2}
        pl={4}
        pr={4}
        cursor="pointer"
        color={active ? sidebarStyles.fgActive : sidebarStyles.fg}
        _hover={!active ? { color: sidebarStyles.fgHover } : {}}
        transition="color 0.15s"
        minH="40px"
      >
        <Box
          w="3px"
          h="20px"
          flexShrink={0}
          borderRadius="full"
          bg={active ? sidebarStyles.fgActive : 'transparent'}
        />
        <Text
          display={open ? 'block' : 'none'}
          fontSize="13px"
          fontWeight={active ? 'semibold' : 'medium'}
          textAlign="left"
          lineHeight={1.35}
        >
          {label}
        </Text>
      </Flex>
    )
  }

  return (
    <Flex
      direction="row"
      align="center"
      justify={open ? 'flex-start' : 'center'}
      gap="14px"
      py={3}
      px={open ? 4 : 2}
      borderRadius="16px"
      cursor="pointer"
      bg={active ? sidebarStyles.activeBg : 'transparent'}
      color={active ? sidebarStyles.activeFg : sidebarStyles.fg}
      boxShadow={active ? '0 6px 18px rgba(0, 0, 0, 0.20)' : 'none'}
      _hover={
        !active
          ? { bg: sidebarStyles.hoverBg, color: sidebarStyles.fgHover }
          : {}
      }
      transition="background 0.15s, color 0.15s"
      minH="52px"
    >
      {Icon && <Icon size={20} style={{ flexShrink: 0 }} />}
      <Text
        display={open ? 'block' : 'none'}
        fontSize="13px"
        fontWeight={active ? 'semibold' : 'medium'}
        textAlign="left"
        lineHeight={1.35}
      >
        {label}
      </Text>
    </Flex>
  )
}

export function NavItem({
  open,
  icon: Icon,
  label,
  path,
  search,
  active,
  sub,
  onClick,
}: NavItemProps) {
  const content = (
    <NavItemContent
      open={open}
      icon={Icon}
      label={label}
      active={active}
      sub={sub}
    />
  )

  const item = onClick ? (
    <Box style={{ textDecoration: 'none', display: 'block' }} onClick={onClick}>
      {content}
    </Box>
  ) : (
    <Link
      to={path as any}
      search={search as any}
      style={{ textDecoration: 'none', display: 'block' }}
    >
      {content}
    </Link>
  )

  // Collapsed, the item is an icon only — the label moves into a tooltip.
  return (
    <Tooltip
      content={label}
      disabled={open}
      showArrow
      openDelay={150}
      closeDelay={0}
      positioning={sidebarTooltipPositioning}
      contentProps={{
        ...sidebarTooltipContentProps,
        px: 3,
        py: 2,
        fontSize: 'sm',
        fontWeight: 'medium',
      }}
    >
      {item}
    </Tooltip>
  )
}
